import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
} from "@mui/material";
import { X } from "lucide-react";
import * as React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  customFieldEntityTypePlural,
  CustomFieldType,
  TargetEntityType,
} from "@/shared/domain/customFields";
import { useCreateCustomField } from "@/shared/domain/customFields/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  RobotoDomainException,
  RobotoLimitExceededException,
} from "@/shared/http/exceptions";

import {
  buildCreateCustomFieldRequest,
  CREATE_FIELD_TYPE_OPTIONS,
  CreateCustomFieldFormValues,
  DEFAULT_CREATE_CUSTOM_FIELD_VALUES,
  makeCreateCustomFieldFormSchema,
} from "./createCustomFieldForm";
import styles from "./customFieldDialog.module.css";
import { customFieldEntityTypeLabel } from "./customFieldPresentation";
import { EnumOptionsEditor } from "./EnumOptionsEditor";
import { FieldTypeSelector } from "./FieldTypeSelector";

interface CreateCustomFieldDialogProps {
  open: boolean;
  entityType: TargetEntityType;
  onClose: () => void;
}

/**
 * Turns a backend failure into the message shown in the form alert. The
 * per-(org, entity_type) quota is the only error worth special copy — anything
 * else falls through to the server message.
 */
function renderSubmitError(
  error: unknown,
  entityType: TargetEntityType,
): React.ReactNode {
  if (error instanceof RobotoLimitExceededException) {
    let limitClause = "its custom field limit";
    try {
      limitClause = `its limit of ${error.limitQuantity} custom fields`;
    } catch {
      // limit_quantity should always be present; keep the generic phrasing if not.
    }
    return (
      <>
        <strong>Custom field limit reached.</strong> Your organization has
        reached {limitClause} for {customFieldEntityTypePlural(entityType)}.
        Delete an existing field to add a new one, or contact us at{" "}
        <Link href="mailto:support@roboto.ai" underline="hover">
          support@roboto.ai
        </Link>{" "}
        to discuss your needs.
      </>
    );
  }
  if (error instanceof RobotoDomainException) {
    return error.message;
  }
  return "Failed to create custom field. Please try again.";
}

export function CreateCustomFieldDialog({
  open,
  entityType,
  onClose,
}: CreateCustomFieldDialogProps) {
  const createCustomField = useCreateCustomField();
  const [submitError, setSubmitError] = React.useState<React.ReactNode | null>(
    null,
  );

  const formSchema = React.useMemo(
    () => makeCreateCustomFieldFormSchema(entityType),
    [entityType],
  );

  const form = useForm<CreateCustomFieldFormValues>({
    defaultValues: DEFAULT_CREATE_CUSTOM_FIELD_VALUES,
    mode: "onChange",
    resolver: zodResolver(formSchema),
  });

  const { reset } = form;
  React.useEffect(() => {
    if (open) {
      reset(DEFAULT_CREATE_CUSTOM_FIELD_VALUES);
      setSubmitError(null);
    }
  }, [open, reset]);

  const fieldType = form.watch("field_type");

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await createCustomField.mutateAsync(
        buildCreateCustomFieldRequest(values, entityType),
      );
      onClose();
    } catch (error) {
      setSubmitError(renderSubmitError(error, entityType));
    }
  });

  return (
    <Dialog
      className={styles.dialog}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={styles.title}>
        New {customFieldEntityTypeLabel(entityType)} Field
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <FormProvider {...form}>
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => void onSubmit(event)}
        >
          <DialogContent dividers className={styles.content}>
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Field Name</span>
              <TextField
                placeholder="firmware_version"
                size="small"
                fullWidth
                error={Boolean(form.formState.errors.field_name)}
                helperText={
                  form.formState.errors.field_name?.message ??
                  "Lowercase letters, digits, and underscores. Cannot be changed later."
                }
                {...form.register("field_name")}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Display Name (optional)</span>
              <TextField
                placeholder="e.g. Firmware Version"
                size="small"
                fullWidth
                error={Boolean(form.formState.errors.display_name)}
                helperText={
                  form.formState.errors.display_name?.message ??
                  "Defaults to the field name when left blank."
                }
                {...form.register("display_name")}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Description (optional)</span>
              <TextField
                placeholder="Describe what this field is used for…"
                size="small"
                fullWidth
                multiline
                minRows={3}
                error={Boolean(form.formState.errors.description)}
                helperText={form.formState.errors.description?.message}
                {...form.register("description")}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Field Type</span>
              <Controller
                control={form.control}
                name="field_type"
                render={({ field }) => (
                  <FieldTypeSelector
                    value={field.value}
                    onChange={field.onChange}
                    options={CREATE_FIELD_TYPE_OPTIONS}
                  />
                )}
              />
            </div>

            {fieldType === CustomFieldType.Enum ? <EnumOptionsEditor /> : null}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button variant="outlined" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              loading={form.formState.isSubmitting}
              disabled={!form.formState.isValid || form.formState.isSubmitting}
            >
              Create Field
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
